import { useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';
import { useExpenseStore } from '../../../store/expenseStore';

const statusV: Record<string, 'success' | 'warning' | 'default' | 'primary' | 'error'> = {
    draft: 'default', submitted: 'primary', approved: 'success', rejected: 'error', paid: 'success', pending: 'warning',
};

const CATEGORIES = ['All', 'Travel', 'Entertainment', 'Software', 'Marketing', 'Mileage', 'Equipment'];

export function ExpenseManagement() {
    const { expenses, isLoading, fetchExpenses, approveExpense } = useExpenseStore();
    const [activeTab, setActiveTab] = useState<'claims' | 'corporate-cards' | 'per-diem'>('claims');
    const [catFilter, setCatFilter] = useState('All');

    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);

    const filtered = catFilter === 'All' ? expenses : expenses.filter(e => e.category === catFilter);
    const totalSubmitted = expenses.filter(e => e.status === 'submitted' || e.status === 'approved' || e.status === 'pending').reduce((s, e) => s + e.amount, 0);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Expense <span className="text-foreground">Management</span></h1>
                    <p className="text-muted-foreground mt-1">Claims · Corporate Cards · Per Diem · Policy Compliance</p>
                </div>
                <div className="flex gap-3">
                    <Button variant="secondary" onClick={() => toast.success('Receipt scanner coming soon (OCR)!')}>Scan Receipt</Button>
                    <Button variant="primary" onClick={() => toast.success('New expense claim!')}>+ New Claim</Button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { label: 'Pending Approval', value: expenses.filter(e => e.status === 'pending' || e.status === 'submitted').length, color: 'text-warning', sub: 'needs your review' },
                    { label: 'Amount Pending', value: `$${totalSubmitted.toLocaleString()}`, color: 'text-primary', sub: 'in pipeline' },
                    { label: 'Total Claims', value: expenses.length, color: 'text-info', sub: 'all time' },
                    { label: 'Paid This Month', value: '$18,450', color: 'text-success', sub: 'reimbursed' },
                ].map((s, i) => (
                    <Card key={i}><CardContent className="p-5">
                        <p className="text-xs text-muted-foreground font-semibold mb-1">{s.label}</p>
                        <p className={`text-3xl font-semibold ${s.color}`}>{s.value}</p>
                        <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>
                    </CardContent></Card>
                ))}
            </div>

            {/* OCR Banner */}
            <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg flex items-center gap-4">
                <span className="text-2xl"></span>
                <div className="flex-1">
                    <p className="font-semibold text-primary">Receipt OCR Scanning Available</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Capture receipts with your phone — AI extracts merchant, date, and amount automatically with confidence scoring.</p>
                </div>
                <Button variant="secondary" size="sm" onClick={() => toast.success('Opening camera...')}>Open Scanner</Button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-border">
                {[
                    { id: 'claims', label: 'Expense Claims' },
                    { id: 'corporate-cards', label: 'Corporate Cards' },
                    { id: 'per-diem', label: 'Per Diem' },
                ].map(t => (
                    <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveTab(t.id as any)}
                        className={`pb-4 px-4 text-sm font-semibold transition-all relative ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                        {t.label}
                        {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                    </Button>
                ))}
            </div>

            {/* Claims */}
            {activeTab === 'claims' && (
                <div className="space-y-4">
                    {/* Category Filter */}
                    <div className="flex gap-2 flex-wrap">
                        {CATEGORIES.map(cat => (
                            <Button variant="unstyled" size="unstyled" key={cat} onClick={() => setCatFilter(cat)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${catFilter === cat ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                                {cat}
                            </Button>
                        ))}
                    </div>
                    <Card>
                        <CardContent className="p-0">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                        <th className="text-left px-6 py-3">Claim #</th>
                                        <th className="text-left px-4 py-3">Employee</th>
                                        <th className="text-left px-4 py-3">Type</th>
                                        <th className="text-left px-4 py-3">Purpose</th>
                                        <th className="text-right px-4 py-3">Amount</th>
                                        <th className="text-center px-4 py-3">Policy</th>
                                        <th className="text-center px-4 py-3">Receipt</th>
                                        <th className="text-center px-4 py-3">Status</th>
                                        <th className="px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border-12">
                                    {filtered.map(exp => (
                                        <tr key={exp.id} className="hover:bg-card/30 transition-colors">
                                            <td className="px-6 py-3 font-mono text-sm text-primary font-semibold">{exp.id.slice(0,8)}</td>
                                            <td className="px-4 py-3">
                                                <p className="font-semibold text-foreground text-xs">{exp.first_name} {exp.last_name}</p>
                                                <p className="text-xs text-muted-foreground">{exp.employee_id.slice(0, 8)}</p>
                                            </td>
                                            <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 bg-card border border-border rounded-lg text-muted-foreground">{exp.category}</span></td>
                                            <td className="px-4 py-3 text-muted-foreground text-sm max-w-xs truncate">{exp.description}</td>
                                            <td className="px-4 py-3 text-right font-semibold">${Number(exp.amount).toLocaleString()}</td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="text-success text-sm"></span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {exp.receipt_url ? <span className="text-success text-sm">Yes</span> : <span className="text-muted-foreground text-sm">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-center"><Badge variant={statusV[exp.status]}>{exp.status}</Badge></td>
                                            <td className="px-4 py-3">
                                                {(exp.status === 'pending' || exp.status === 'submitted') && (
                                                    <Button variant="unstyled" size="unstyled" className="text-xs text-success hover:text-success/70 font-semibold" onClick={async () => {
                                                        await approveExpense(exp.id);
                                                        toast.success(`Approved!`);
                                                    }}>Approve</Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Corp Cards */}
            {activeTab === 'corporate-cards' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                        { holder: 'Alex Kumar', last4: '4242', limit: 10000, spent: 4200, type: 'Visa Platinum', status: 'active' },
                        { holder: 'Sarah Jones', last4: '1234', limit: 5000, spent: 850, type: 'Mastercard Gold', status: 'active' },
                        { holder: 'Mike R.', last4: '5678', limit: 20000, spent: 15000, type: 'Amex Corporate', status: 'active' },
                    ].map((card, i) => {
                        const usePct = (card.spent / card.limit) * 100;
                        return (
                            <Card key={i} className={`border ${usePct > 80 ? 'border-destructive/30' : 'border-border'}`}>
                                <CardContent className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <p className="text-xs text-muted-foreground font-semibold">{card.type}</p>
                                            <p className="font-semibold text-foreground mt-1">•••• •••• •••• {card.last4}</p>
                                        </div>
                                        <Badge variant={card.status === 'active' ? 'success' : 'error'}>{card.status}</Badge>
                                    </div>
                                    <p className="text-sm font-semibold text-muted-foreground mb-4">{card.holder}</p>
                                    <div className="mb-2 flex justify-between text-xs font-semibold">
                                        <span className="text-muted-foreground">Spent this month</span>
                                        <span className={usePct > 80 ? 'text-destructive' : 'text-foreground'}>${card.spent.toLocaleString()} / ${card.limit.toLocaleString()}</span>
                                    </div>
                                    <div className="h-2 bg-background rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full ${usePct > 80 ? 'bg-destructive' : 'bg-primary'}`} style={{ width: `${usePct}%` }} />
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-2 text-right">{usePct.toFixed(0)}% of limit</p>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Per Diem */}
            {activeTab === 'per-diem' && (
                <Card>
                    <CardContent className="p-8">
                        <h3 className="text-foreground mb-6 font-display text-card-title">Per Diem Rates by Location</h3>
                        <div className="space-y-4">
                            {[
                                { location: 'New York, USA', meals: 79, lodging: 350, incidentals: 20, total: 449 },
                                { location: 'London, UK', meals: 65, lodging: 280, incidentals: 17, total: 362 },
                                { location: 'Mumbai, India', meals: 35, lodging: 120, incidentals: 10, total: 165 },
                                { location: 'Singapore', meals: 55, lodging: 200, incidentals: 15, total: 270 },
                            ].map((rate, i) => (
                                <div key={i} className="flex items-center justify-between p-4 bg-card rounded-lg border border-border">
                                    <p className="font-semibold text-foreground">{rate.location}</p>
                                    <div className="flex gap-6 text-sm text-center">
                                        <div><p className="text-xs text-muted-foreground font-semibold">Meals</p><p className="font-semibold text-foreground">${rate.meals}</p></div>
                                        <div><p className="text-xs text-muted-foreground font-semibold">Lodging</p><p className="font-semibold text-foreground">${rate.lodging}</p></div>
                                        <div><p className="text-xs text-muted-foreground font-semibold">Incidentals</p><p className="font-semibold text-foreground">${rate.incidentals}</p></div>
                                        <div><p className="text-xs text-muted-foreground font-semibold">Total/Day</p><p className="font-semibold text-success">${rate.total}</p></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
